import React, { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';

declare global {
    interface Window {
        fbq: any;
        _fbq: any;
    }
}

type StandardEvent =
    | 'PageView'
    | 'ViewContent'
    | 'Search'
    | 'AddToCart'
    | 'AddToWishlist'
    | 'InitiateCheckout'
    | 'AddPaymentInfo'
    | 'Purchase'
    | 'Lead'
    | 'CompleteRegistration';

interface EventData {
    value?: number;
    currency?: string;
    content_name?: string;
    content_ids?: string[];
    content_type?: string;
    [key: string]: any;
}

interface AdvancedMatchingData {
    em?: string;   // email
    fn?: string;   // first name
    ln?: string;   // last name
    ph?: string;   // phone
    external_id?: string;
    ge?: string;   // gender
    db?: string;   // date of birth
    ct?: string;   // city
    st?: string;   // state
    zp?: string;   // zip
    country?: string;
}

interface ClickIds {
    fbc: string | null;  // Facebook Click ID
    fbp: string | null;  // Facebook Browser ID
}

interface FacebookPixelContextType {
    trackEvent: (eventName: StandardEvent, data?: EventData) => void;
    trackCustomEvent: (eventName: string, data?: Record<string, any>) => void;
    setUserData: (data: AdvancedMatchingData) => void;
    getClickIds: () => ClickIds;
    grantConsent: () => void;
    revokeConsent: () => void;
    hasConsent: boolean;
    isInitialized: boolean;
}

interface FacebookPixelProviderProps {
    children: ReactNode;
    pixelId: string;
    autoPageView?: boolean;
    requireConsent?: boolean;
    debug?: boolean;
}

// ── Helper: read a cookie by name ────────────────────────────────────
function getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
}

// ── Helper: capture fbclid from URL and build fbc value ──────────────
function captureFbclid(): string | null {
    if (typeof window === 'undefined') return null;

    const STORAGE_KEY = 'fb_click_id';

    // Try to get fbclid from current URL
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get('fbclid');

    if (fbclid) {
        // Build fbc in the proper format: fb.1.{timestamp_ms}.{fbclid}
        const fbc = `fb.1.${Date.now()}.${fbclid}`;
        localStorage.setItem(STORAGE_KEY, fbc);
        return fbc;
    }

    // Fall back to _fbc cookie (set by the pixel itself)
    const cookieFbc = getCookie('_fbc');
    if (cookieFbc) {
        localStorage.setItem(STORAGE_KEY, cookieFbc);
        return cookieFbc;
    }

    // Fall back to previously stored value
    return localStorage.getItem(STORAGE_KEY);
}

const FacebookPixelContext = createContext<FacebookPixelContextType | undefined>(
    undefined
);

export const FacebookPixelProvider: React.FC<FacebookPixelProviderProps> = ({
    children,
    pixelId,
    autoPageView = true,
    requireConsent = false,
    debug = false,
}) => {
    const [hasConsent, setHasConsent] = useState<boolean>(!requireConsent);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const eventQueueRef = useRef<Array<{ type: 'track' | 'trackCustom'; name: string; data: any }>>([]);
    const initRef = useRef(false);
    const userDataRef = useRef<AdvancedMatchingData>({});
    const fbcRef = useRef<string | null>(captureFbclid());

    useEffect(() => {
        if (requireConsent && typeof window !== 'undefined') {
            const savedConsent = localStorage.getItem('fb_pixel_consent');
            if (savedConsent === 'granted') {
                setHasConsent(true);
            }
        }
    }, [requireConsent]);

    const initPixel = useCallback(() => {
        if (typeof window === 'undefined' || !hasConsent || initRef.current) {
            return;
        }
        initRef.current = true;

        try {
            (function (f: any, b: Document, e: string, v: string, n?: any, t?: any, s?: any) {
                if (f.fbq) return;
                n = f.fbq = function () {
                    n.callMethod
                        ? n.callMethod.apply(n, arguments)
                        : n.queue.push(arguments);
                };
                if (!f._fbq) f._fbq = n;
                n.push = n;
                n.loaded = true;
                n.version = '2.0';
                n.queue = [];
                t = b.createElement(e);
                t.async = true;
                t.src = v;
                s = b.getElementsByTagName(e)[0];
                s.parentNode?.insertBefore(t, s);
            })(
                window,
                document,
                'script',
                'https://connect.facebook.net/en_US/fbevents.js'
            );

            // Pass advanced matching data if available
            const matchingData = userDataRef.current;
            const hasUserData = Object.values(matchingData).some(v => v);
            window.fbq('init', pixelId, hasUserData ? matchingData : {});

            if (autoPageView) {
                window.fbq('track', 'PageView');
            }

            // Flush queued events
            eventQueueRef.current.forEach(({ type, name, data }) => {
                window.fbq(type, name, data);
            });
            eventQueueRef.current = [];

            setIsInitialized(true);

            if (debug) {
                console.log(`[Facebook Pixel] Initialized with ID: ${pixelId}`);
            }
        } catch (error) {
            console.error('[Facebook Pixel] Initialization error:', error);
        }
    }, [pixelId, hasConsent, autoPageView, debug]);

    useEffect(() => {
        if (!hasConsent) return;

        // Delay FB Pixel loading by 5s or first user interaction (with yield)
        const timer = setTimeout(initPixel, 5000);
        const events = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;
        const onInteraction = () => {
            clearTimeout(timer);
            // Yield to browser before loading so interaction stays responsive
            setTimeout(initPixel, 100);
            events.forEach(e => document.removeEventListener(e, onInteraction));
        };
        events.forEach(e => document.addEventListener(e, onInteraction, { once: true, passive: true }));

        return () => {
            clearTimeout(timer);
            events.forEach(e => document.removeEventListener(e, onInteraction));
        };
    }, [hasConsent, initPixel]);

    const grantConsent = (): void => {
        setHasConsent(true);
        if (typeof window !== 'undefined') {
            localStorage.setItem('fb_pixel_consent', 'granted');
        }
        if (debug) {
            console.log('[Facebook Pixel] Consent granted');
        }
    };

    const revokeConsent = (): void => {
        setHasConsent(false);
        setIsInitialized(false);
        initRef.current = false;
        if (typeof window !== 'undefined') {
            localStorage.setItem('fb_pixel_consent', 'revoked');
        }
        if (debug) {
            console.log('[Facebook Pixel] Consent revoked');
        }
    };

    const getClickIds = useCallback((): ClickIds => {
        // fbc: from captured fbclid or _fbc cookie
        const fbc = fbcRef.current || getCookie('_fbc');
        // fbp: from _fbp cookie (set automatically by the pixel)
        const fbp = getCookie('_fbp');
        return { fbc, fbp };
    }, []);

    const setUserData = useCallback((data: AdvancedMatchingData): void => {
        // Merge new data with existing
        userDataRef.current = { ...userDataRef.current, ...data };

        // If pixel is already initialized, re-init with updated user data
        if (typeof window !== 'undefined' && window.fbq && initRef.current) {
            const matchingData = userDataRef.current;
            const hasData = Object.values(matchingData).some(v => v);
            if (hasData) {
                window.fbq('init', pixelId, matchingData);
                if (debug) {
                    console.log('[Facebook Pixel] Advanced matching updated:', Object.keys(matchingData).filter(k => matchingData[k as keyof AdvancedMatchingData]));
                }
            }
        }
    }, [pixelId, debug]);

    const trackEvent = (eventName: StandardEvent, data: EventData = {}): void => {
        if (!hasConsent) {
            if (debug) {
                console.warn('[Facebook Pixel] Tracking blocked - no consent');
            }
            return;
        }

        if (typeof window !== 'undefined' && window.fbq) {
            window.fbq('track', eventName, data);
            if (debug) {
                console.log(`[Facebook Pixel] Event tracked: ${eventName}`, data);
            }
        } else {
            eventQueueRef.current.push({ type: 'track', name: eventName, data });
        }
    };

    const trackCustomEvent = (
        eventName: string,
        data: Record<string, any> = {}
    ): void => {
        if (!hasConsent) {
            if (debug) {
                console.warn('[Facebook Pixel] Tracking blocked - no consent');
            }
            return;
        }

        if (typeof window !== 'undefined' && window.fbq) {
            window.fbq('trackCustom', eventName, data);
            if (debug) {
                console.log(`[Facebook Pixel] Custom event tracked: ${eventName}`, data);
            }
        } else {
            eventQueueRef.current.push({ type: 'trackCustom', name: eventName, data });
        }
    };

    return (
        <FacebookPixelContext.Provider
            value={{
                trackEvent,
                trackCustomEvent,
                setUserData,
                getClickIds,
                grantConsent,
                revokeConsent,
                hasConsent,
                isInitialized,
            }}
        >
            {children}
        </FacebookPixelContext.Provider>
    );
};

export const useFacebookPixel = (): FacebookPixelContextType => {
    const context = useContext(FacebookPixelContext);
    if (!context) {
        throw new Error(
            'useFacebookPixel must be used within FacebookPixelProvider'
        );
    }
    return context;
};

// Cookie consent banner component
export const CookieConsentBanner: React.FC = () => {
    const { hasConsent, grantConsent } = useFacebookPixel();
    const [visible, setVisible] = useState<boolean>(!hasConsent);

    if (!visible || hasConsent) {
        return null;
    }

    const handleAccept = (): void => {
        grantConsent();
        setVisible(false);
    };

    const handleDecline = (): void => {
        setVisible(false);
    };

    const bannerStyle: React.CSSProperties = {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#2c3e50',
        color: 'white',
        padding: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 9999,
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
    };

    const buttonStyle: React.CSSProperties = {
        padding: '10px 20px',
        marginLeft: '10px',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
    };

    return (
        <div style={bannerStyle}>
            <p style={{ margin: 0, flex: 1 }}>
                We use cookies and tracking pixels to improve your experience and analyze site
                traffic. By clicking "Accept", you consent to our use of cookies.
            </p>
            <div>
                <button
                    onClick={handleDecline}
                    style={{
                        ...buttonStyle,
                        backgroundColor: '#95a5a6',
                        color: 'white',
                    }}
                >
                    Decline
                </button>
                <button
                    onClick={handleAccept}
                    style={{
                        ...buttonStyle,
                        backgroundColor: '#27ae60',
                        color: 'white',
                    }}
                >
                    Accept
                </button>
            </div>
        </div>
    );
};

export type { StandardEvent, EventData, FacebookPixelContextType, AdvancedMatchingData, ClickIds };
